/**
 * DimmerKnob - Horizontal slider for PWM actuators (lights and pumps)
 *
 * Features:
 * - Drag slider 0-100%
 * - Optional power toggle button
 * - Optional preset value pills
 * - Optional timed-run section with duration pills
 */

import React, { useCallback, useRef, useState, useEffect } from 'react';
import { Power } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface DimmerKnobProps {
  /** Current value 0-100 */
  value: number;
  /** Called during drag with intermediate values */
  onChange?: (value: number) => void;
  /** Called when drag ends - commit the final value */
  onChangeEnd: (value: number) => void;
  /** Whether the control is disabled */
  disabled?: boolean;
  /** Accent color for the filled track */
  color?: string;
  /** Size reserved (unused, kept for API compat) */
  size?: number;
  /** Label shown when value > 0 (defaults to i18n dimmer.on) */
  onLabel?: string;
  /** Label shown when value === 0 (defaults to i18n dimmer.off) */
  offLabel?: string;
  /** If provided, renders a power toggle button and calls this on click */
  onToggle?: () => void;
  /** Preset value pills rendered below the slider (e.g. [10, 25, 50, 75, 100]) */
  presets?: number[];
  /** If provided, renders a timed-run row with duration pills below presets */
  onRunTimed?: (value: number, durationSec: number) => void;
  /** Duration pills in seconds (default: 5, 10, 30, 60, 300) */
  durationPresets?: number[];
}

const formatDuration = (sec: number): string => {
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)} min`;
  return `${Math.floor(sec / 3600)}h`;
};

export const DimmerKnob: React.FC<DimmerKnobProps> = ({
  value,
  onChange,
  onChangeEnd,
  disabled = false,
  color = '#eab308',
  onLabel,
  offLabel,
  onToggle,
  presets,
  onRunTimed,
  durationPresets = [5, 10, 30, 60, 300],
}) => {
  const { t } = useTranslation('components');
  const trackRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [localValue, setLocalValue] = useState(value);
  const valueRef = useRef(value);

  useEffect(() => {
    if (!isDragging) {
      setLocalValue(value);
      valueRef.current = value;
    }
  }, [value, isDragging]);

  const getValueFromX = useCallback(
    (clientX: number): number => {
      const track = trackRef.current;
      if (!track) return valueRef.current;
      const rect = track.getBoundingClientRect();
      const pct = ((clientX - rect.left) / rect.width) * 100;
      return Math.round(Math.max(0, Math.min(100, pct)) * 10) / 10;
    },
    [],
  );

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (disabled) return;
      e.preventDefault();
      (e.target as Element).setPointerCapture(e.pointerId);
      setIsDragging(true);
      const newValue = getValueFromX(e.clientX);
      setLocalValue(newValue);
      valueRef.current = newValue;
      onChange?.(newValue);
    },
    [disabled, getValueFromX, onChange],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!isDragging || disabled) return;
      const newValue = getValueFromX(e.clientX);
      setLocalValue(newValue);
      valueRef.current = newValue;
      onChange?.(newValue);
    },
    [isDragging, disabled, getValueFromX, onChange],
  );

  const handlePointerUp = useCallback(
    () => {
      if (!isDragging) return;
      setIsDragging(false);
      onChangeEnd(valueRef.current);
    },
    [isDragging, onChangeEnd],
  );

  const displayValue = localValue;
  const isOn = displayValue > 0;
  const activeLabel = isOn ? (onLabel ?? t('dimmer.on')) : (offLabel ?? t('dimmer.off'));

  return (
    <div className="w-full px-1 select-none space-y-2">
      {/* Header: optional toggle button + status label + value % */}
      <div className="flex items-center gap-2">
        {onToggle && (
          <button
            onClick={(e) => { e.stopPropagation(); onToggle(); }}
            disabled={disabled}
            aria-label={isOn ? t('dimmer.turnOff') : t('dimmer.turnOn')}
            className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-colors focus-visible:ring-2 focus-visible:ring-offset-1 ${
              isOn
                ? 'text-white'
                : 'text-gray-400 hover:text-gray-600 dark:hover:text-zinc-300 bg-gray-100 dark:bg-zinc-700 hover:bg-gray-200 dark:hover:bg-zinc-600'
            }`}
            style={isOn ? { backgroundColor: color } : undefined}
          >
            <Power size={13} />
          </button>
        )}
        <span
          className="flex-1 text-xs font-bold uppercase tracking-wider"
          style={{ color: isOn ? color : '#9ca3af' }}
        >
          {activeLabel}
        </span>
        <span
          className="text-sm font-bold tabular-nums"
          style={{ color: isOn ? color : '#9ca3af' }}
        >
          {displayValue}%
        </span>
      </div>

      {/* Slider track */}
      <div
        ref={trackRef}
        className="relative h-8 cursor-pointer rounded-full"
        style={{ touchAction: 'none' }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      >
        {/* Background track */}
        <div className="absolute inset-0 rounded-full bg-gray-200 dark:bg-zinc-700/60 overflow-hidden">
          {/* Filled portion */}
          <div
            className="absolute inset-y-0 left-0 rounded-full transition-[width] duration-75"
            style={{
              width: `${displayValue}%`,
              background: isOn
                ? `linear-gradient(90deg, ${color}80, ${color})`
                : '#9ca3af40',
              boxShadow: isOn ? `0 0 12px ${color}50` : 'none',
            }}
          />
        </div>

        {/* Thumb */}
        <div
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 transition-[left] duration-75"
          style={{ left: `${displayValue}%` }}
        >
          <div
            className="w-6 h-6 rounded-full border-2 border-white shadow-md transition-transform duration-150"
            style={{
              backgroundColor: isOn ? color : '#9ca3af',
              transform: isDragging ? 'scale(1.2)' : 'scale(1)',
              boxShadow: isOn
                ? `0 0 8px ${color}80, 0 2px 4px rgba(0,0,0,0.2)`
                : '0 2px 4px rgba(0,0,0,0.2)',
            }}
          />
        </div>

        {/* Disabled overlay */}
        {disabled && (
          <div className="absolute inset-0 bg-gray-500/20 rounded-full cursor-not-allowed" />
        )}
      </div>

      {/* Preset value pills */}
      {presets && presets.length > 0 && (
        <div className="flex gap-1 flex-wrap">
          {presets.map((preset) => (
            <button
              key={preset}
              onClick={(e) => { e.stopPropagation(); onChangeEnd(preset); }}
              disabled={disabled}
              className={`px-2 py-0.5 rounded-full text-xs font-medium transition-colors ${
                Math.round(displayValue) === preset
                  ? 'text-white'
                  : 'bg-gray-100 dark:bg-zinc-700 text-gray-600 dark:text-zinc-300 hover:bg-gray-200 dark:hover:bg-zinc-600'
              }`}
              style={Math.round(displayValue) === preset ? { backgroundColor: color } : undefined}
            >
              {preset}%
            </button>
          ))}
        </div>
      )}

      {/* Timed run section */}
      {onRunTimed && (
        <div className="flex items-center gap-1.5 flex-wrap pt-1 border-t border-gray-100 dark:border-zinc-800">
          <span className="text-xs text-gray-500 dark:text-zinc-400 flex-shrink-0">
            {t('dimmer.runTimed')}:
          </span>
          {durationPresets.map((sec) => (
            <button
              key={sec}
              onClick={(e) => {
                e.stopPropagation();
                onRunTimed(valueRef.current > 0 ? valueRef.current : 100, sec);
              }}
              disabled={disabled}
              className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-zinc-700 text-gray-600 dark:text-zinc-300 hover:bg-gray-200 dark:hover:bg-zinc-600 transition-colors"
            >
              {formatDuration(sec)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default DimmerKnob;
